/**
 * The Bundles tab of a campaign: what a seat filed together for one campaign
 * and day – email items (`email_submit_draft` with `bundle`) and tasks such
 * as LinkedIn messages or calls (`task_propose` with `bundle`). The list
 * shows each bundle's state; `?bundle=<id>` opens one. Every email item is
 * decided on its own – Approve (the seat creates the mailbox draft, the owner
 * sends it) or Request changes – and "Approve the rest" takes the remaining
 * pending email items, leaving expired ones out. A decision on one item never
 * holds another. Tasks are the owner's own actions: open the chat, send,
 * Sent ✓ (or Done / Decline…); "Work through tasks" runs them one by one on
 * the Tasks page. Comments, versions, snooze, withdraw and triage of an email
 * live on its review page (`/team/email-drafts/:id`).
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { adminApi } from "../../api/admin";
import { apiErrorDetail } from "../../api/client";
import {
  useApproveRest,
  useConsoleBundle,
  useConsoleInvalidate,
  type Bundle,
  type BundleItem,
} from "../../api/console";
import { Age } from "../../components/Age";
import { Markdown } from "../../components/Markdown";
import { EmailAttachments } from "../../components/review/EmailAttachments";
import { useConfirm } from "../../components/confirm/context";
import { Pill } from "../../components/Pill";
import { PriorityBadge } from "../../components/PriorityBadge";
import { QueryBoundary } from "../../components/QueryBoundary";
import { useToast } from "../../components/toast/context";
import type { HumanTask } from "../../api/tasks";
import { TaskCard } from "../../components/tasks/TaskCard";
import { reviewStatusTone, reviewUrl } from "../../lib/aiTeam";
import { ageSeconds, berlinDateTime, doneSummary, retiredLabel, splitBundleItems } from "../../lib/console";
import { emailOutcome, signatureBlock, translationLabel } from "../../lib/emailDrafts";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import { useTaskActions } from "../../lib/hooks/useTaskActions";
import { finishOutcome, finishQuestion, type FinishContext } from "../../lib/reviewFinish";
import { openPrimary } from "../../lib/taskActions";
import { bundleBatchKey } from "../../lib/taskGroups";

const STATUS_LABEL: Record<string, string> = {
  pending: "waiting for you",
  changes_requested: "changes requested",
  approved: "approved",
  done: "drafted",
  sent: "sent",
  rejected: "rejected",
  withdrawn: "withdrawn",
  tasks_open: "tasks open",
  tasks_done: "tasks done",
};

const OPEN_TASK = ["proposed", "scheduled", "awaiting_manual"];

/** "2 emails · 3 tasks". */
function itemsLine(b: Bundle): string {
  const tasks = b.tasks?.length ?? 0;
  const parts = [];
  if (b.item_count || !tasks) parts.push(`${b.item_count} email${b.item_count === 1 ? "" : "s"}`);
  if (tasks) parts.push(`${tasks} task${tasks === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/** The Tasks page's work-through key of this bundle. */
function workKey(b: Bundle): string {
  return b.tasks?.find((t) => t.batch?.key)?.batch?.key ?? bundleBatchKey(b.id);
}

function countsLine(counts: Bundle["counts"]): string {
  return Object.entries(counts)
    .filter(([, n]) => (n ?? 0) > 0)
    .map(([k, n]) => `${n} ${STATUS_LABEL[k] ?? k.replace(/_/g, " ")}`)
    .join(" · ");
}

export function BundleList({
  bundles,
  seat,
  onOpen,
}: {
  bundles: Bundle[];
  seat: string;
  onOpen: (id: string) => void;
}) {
  const [nowMs] = useState(() => Date.now());
  if (!bundles.length) {
    return (
      <div className="panel text-sm text-ink-low">
        No bundle yet. {seat} files the next one with email_submit_draft and its bundle argument.
      </div>
    );
  }
  return (
    <div className="panel panel--flush overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Bundle</th>
            <th>Seat</th>
            <th>Items</th>
            <th className="num">Expired</th>
            <th>Priority</th>
            <th>State</th>
            <th className="text-right">Age</th>
          </tr>
        </thead>
        <tbody>
          {bundles.map((b) => {
            const pending = (b.counts.pending ?? 0) + (b.counts.tasks_open ?? 0);
            return (
              <tr key={b.id} className="cursor-pointer align-top" onClick={() => onOpen(b.id)}>
                <td>
                  <button
                    type="button"
                    className="font-heading font-semibold text-ink hover:text-accent text-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(b.id);
                    }}
                  >
                    {b.title}
                  </button>
                  {b.summary ? <div className="text-xs text-ink-low">{b.summary}</div> : null}
                </td>
                <td className="font-mono text-xs">{b.agent_slug ?? "–"}</td>
                <td className="text-sm">
                  <span className="tnum">{itemsLine(b)}</span>
                  <div className="text-xs text-ink-low">{countsLine(b.counts)}</div>
                </td>
                <td className="num">{b.expired_count || "–"}</td>
                <td>{b.top_priority ? <PriorityBadge priority={b.top_priority} /> : null}</td>
                <td>
                  {b.state === "open" ? (
                    <Pill value={pending ? `${pending} waiting for you` : "with the seat"} tone="signal" />
                  ) : (
                    <Pill value="decided" tone="ok" />
                  )}
                </td>
                <td className="text-right">
                  <Age seconds={ageSeconds(b.created_at, nowMs)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function BundleDetail({
  bundleId,
  onBack,
  onOpenThread,
}: {
  bundleId: string;
  onBack: () => void;
  onOpenThread: (threadId: string) => void;
}) {
  const query = useConsoleBundle(bundleId);
  const approveRest = useApproveRest();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <>
      <button type="button" className="btn btn-sm btn-ghost mb-3" onClick={onBack}>
        ← All bundles
      </button>
      <QueryBoundary query={query}>
        {(bundle) => {
          const rest = bundle.items.filter(
            (i) => i.status === "pending" && !i.expired && !signatureBlock(i.email_meta),
          );
          const expiredPending = bundle.items.filter((i) => i.status === "pending" && i.expired).length;
          const unsignedPending = bundle.items.filter(
            (i) => i.status === "pending" && !i.expired && signatureBlock(i.email_meta),
          ).length;
          const { live, done, retired } = splitBundleItems(bundle.items);
          const tasks = bundle.tasks ?? [];
          const openTasks = tasks.filter((t) => OPEN_TASK.includes(t.status)).length;
          const onApproveRest = async () => {
            const ok = await confirm({
              title: `Approve the remaining ${rest.length} item${rest.length === 1 ? "" : "s"} as drafts?`,
              body:
                `${bundle.agent_slug ?? "The seat"} creates each mailbox draft with exactly the approved content; you send them from Outlook.` +
                (expiredPending ? ` ${expiredPending} expired item${expiredPending === 1 ? "" : "s"} stay out – their offered dates ran out.` : "") +
                (unsignedPending ? ` ${unsignedPending} item${unsignedPending === 1 ? "" : "s"} stay out – they wait for your signature.` : ""),
              confirmLabel: `Approve ${rest.length}`,
            });
            if (!ok) return;
            approveRest.mutate(bundle.id, {
              onSuccess: (r) => {
                const parts = [`${r.approved.length} approved`];
                if (r.skipped.length) parts.push(`${r.skipped.length} left out`);
                if (r.failed.length) parts.push(`${r.failed.length} failed`);
                if (r.failed.length) toast.error(`${parts.join(" · ")} – ${r.failed[0].error}`);
                else toast.ok(parts.join(" · "));
              },
              onError: (e) => toast.error(e),
            });
          };
          return (
            <div>
              <div className="panel mb-4">
                <div className="flex flex-wrap items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-semibold text-ink">{bundle.title}</div>
                    {bundle.summary ? <div className="text-sm text-ink-medium mt-1">{bundle.summary}</div> : null}
                    <div className="text-xs text-ink-low mt-1">
                      <span className="font-mono">{bundle.agent_slug ?? "–"}</span> · created{" "}
                      <span className="font-mono">{berlinDateTime(bundle.created_at)}</span>
                      {countsLine(bundle.counts) ? ` · ${countsLine(bundle.counts)}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {openTasks ? (
                      <Link to={`/tasks?work=${encodeURIComponent(workKey(bundle))}`} className="btn btn-second btn-sm">
                        Work through tasks · {openTasks}
                      </Link>
                    ) : null}
                    {bundle.items.length ? (
                      <button
                        type="button"
                        className="btn btn-main btn-sm"
                        disabled={!rest.length || approveRest.isPending}
                        onClick={() => void onApproveRest()}
                      >
                        Approve the rest · {rest.length}
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="text-xs text-ink-low mt-2">
                  Each item is decided on its own. A change to one item never holds the others back.
                </div>
              </div>
              <div className="space-y-4">
                {live.map((item) => (
                  <BundleItemCard key={item.id} item={item} onOpenThread={onOpenThread} />
                ))}
                <FoldedItems label="Done" hint={doneSummary(done)} items={done} onOpenThread={onOpenThread} />
                <FoldedItems
                  label={retiredLabel(retired)}
                  hint="never sent"
                  items={retired}
                  onOpenThread={onOpenThread}
                />
              </div>
              {tasks.length ? (
                <>
                  <h3 className="font-heading font-semibold text-ink mt-6 mb-2">
                    Tasks · <span className="tnum">{openTasks}</span> open
                  </h3>
                  <div className="space-y-4">
                    {tasks.map((task) => (
                      <BundleTaskCard key={task.task_id} task={task} />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          );
        }}
      </QueryBoundary>
    </>
  );
}

/** Emails that need nothing from the owner anymore, folded below the live ones. */
function FoldedItems({
  label,
  hint,
  items,
  onOpenThread,
}: {
  label: string;
  hint: string;
  items: BundleItem[];
  onOpenThread: (threadId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <section className="panel panel--flush">
      <button type="button" className="queue-group-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden>{open ? "▾" : "▸"}</span>
        <span className="font-semibold">{label}</span>
        <span className="font-mono text-xs text-ink-low">{items.length}</span>
        <span className="text-xs text-ink-low">– {hint}</span>
      </button>
      {open ? (
        <div className="space-y-4 p-4 border-t border-rule">
          {items.map((item) => (
            <BundleItemCard key={item.id} item={item} onOpenThread={onOpenThread} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function BundleItemCard({ item, onOpenThread }: { item: BundleItem; onOpenThread: (threadId: string) => void }) {
  const [nowMs] = useState(() => Date.now());
  const finish = adminApi.aiTeam.finishReview();
  const invalidate = useConsoleInvalidate();
  const confirm = useConfirm();
  const toast = useToast();
  const [composing, setComposing] = useState(false);
  const note = useDraft(draftKey("bundle-item", item.id, item.content_version));
  const meta = item.email_meta ?? {};
  const subject = meta.subject || item.title;
  const approveBlocked = signatureBlock(meta);
  const pending = item.status === "pending";
  const ctx = (feedbackCount: number): FinishContext => ({
    author: item.agent_slug,
    isEmail: true,
    feedbackCount,
    mailbox: meta.mailbox_address,
    subject,
  });

  const approve = async () => {
    if (!(await confirm(finishQuestion("approve", "draft", ctx(0))))) return;
    finish.mutate(
      { reviewId: item.id, decision: "approve", delivery: "draft" },
      {
        onSuccess: (r) => {
          const outcome = finishOutcome(r, ctx(0));
          if (outcome.error) toast.error(outcome.text);
          else toast.ok(outcome.text);
        },
        onError: (e) => toast.error(`Approve failed: ${apiErrorDetail(e)}`),
        onSettled: () => void invalidate(),
      },
    );
  };

  const requestChanges = async () => {
    const text = note.draft.trim();
    if (!text) return;
    if (!(await confirm(finishQuestion("request_changes", undefined, ctx(1))))) return;
    try {
      const r = await finish.mutateAsync({ reviewId: item.id, decision: "request_changes", feedback_md: text });
      note.clearDraft();
      setComposing(false);
      toast.ok(finishOutcome(r, ctx(1)).text);
    } catch (e) {
      toast.error(`Request failed: ${apiErrorDetail(e)}`);
    } finally {
      void invalidate();
    }
  };

  return (
    <div className="panel">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-mono text-sm text-ink">{(meta.to ?? []).join(", ") || "–"}</span>
        <PriorityBadge priority={item.priority} />
        {item.topic ? <span className="text-xs text-ink-low">{item.topic.name}</span> : null}
        <span className="text-xs text-ink-low font-mono">v{item.content_version}</span>
        <span className="ml-auto flex items-center gap-2">
          <Pill value={emailOutcome(item) ?? STATUS_LABEL[item.status] ?? item.status} tone={reviewStatusTone(item.status)} />
          <Age seconds={ageSeconds(item.created_at ?? item.submitted_at, nowMs)} />
        </span>
      </div>

      {item.expired ? (
        <div className="callout callout--warn text-sm mb-3">
          The offered dates ran out ({berlinDateTime(item.valid_until)}). Request a new version – "Approve the rest" leaves
          this item out.
        </div>
      ) : item.valid_until ? (
        <div className="text-xs text-ink-low mb-2">Offered dates valid until {berlinDateTime(item.valid_until)}</div>
      ) : null}

      {item.inbound ? (
        <details className="callout callout--info text-sm mb-3">
          <summary className="cursor-pointer">
            Answers <span className="font-semibold">{item.inbound.from ?? "unknown sender"}</span>{" "}
            <span className="font-mono text-xs">{berlinDateTime(item.inbound.at)}</span>
            {item.inbound.subject ? ` – ${item.inbound.subject}` : ""}
          </summary>
          <div className="whitespace-pre-wrap mt-2 text-ink-medium">{item.inbound.text}</div>
          {meta.reply_translation_md ? (
            <div className="mt-2 pt-2 border-t border-rule">
              <div className="text-xs text-ink-low mb-1">Translation by the employee</div>
              <Markdown text={meta.reply_translation_md} />
            </div>
          ) : null}
        </details>
      ) : meta.reply_to_message_id ? (
        <div className="text-xs text-ink-low mb-2">A reply – the message it answers is not in the CRM log; the review page loads it from the mailbox.</div>
      ) : null}

      <div className="text-sm font-semibold text-ink">{subject}</div>
      <div className="text-sm whitespace-pre-wrap mt-1">{item.content_md ?? ""}</div>
      {meta.translation_md ? (
        <div className="mt-3 pt-2 border-t border-rule text-sm">
          <div className="text-xs text-ink-low mb-1">
            {translationLabel(meta.translation_language)} translation – not sent
          </div>
          <Markdown text={meta.translation_md} />
        </div>
      ) : null}
      <div className="mt-3">
        <EmailAttachments meta={meta} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {pending ? (
          <>
            <button
              type="button"
              className="btn btn-main btn-sm"
              disabled={finish.isPending || !!approveBlocked}
              title={approveBlocked ?? undefined}
              onClick={() => void approve()}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={finish.isPending}
              onClick={() => setComposing((v) => !v)}
            >
              Request changes
            </button>
          </>
        ) : item.sent_directly ? (
          <span className="text-xs text-ink-low">
            Sent from <span className="font-mono">{item.sent_directly.mailbox}</span> ·{" "}
            <span className="font-mono">{berlinDateTime(item.sent_directly.sent_at)}</span>
          </span>
        ) : item.status === "changes_requested" ? (
          <span className="text-xs text-ink-low">Sent back – {item.agent_slug ?? "the seat"} revises and resubmits.</span>
        ) : item.status === "approved" ? (
          <span className="text-xs text-ink-low">Approved – {item.agent_slug ?? "the seat"} creates the mailbox draft.</span>
        ) : item.status === "done" ? (
          <span className="text-xs text-ink-low">Draft created – you send it from Outlook.</span>
        ) : item.status === "withdrawn" ? (
          <span className="text-xs text-ink-low">Withdrawn by {item.agent_slug ?? "the seat"} – it never goes out.</span>
        ) : item.status === "rejected" ? (
          <span className="text-xs text-ink-low">Rejected – it never goes out.</span>
        ) : null}
        <Link to={reviewUrl(item.id, "email")} className="link text-sm ml-auto">
          Open review →
        </Link>
        {item.thread_id ? (
          <button type="button" className="link text-sm" onClick={() => onOpenThread(item.thread_id as string)}>
            Thread →
          </button>
        ) : null}
      </div>

      {pending && composing ? (
        <div className="mt-3">
          <textarea
            className="input w-full"
            rows={3}
            aria-label="What should change"
            placeholder="What should change – the seat revises and resubmits."
            value={note.draft}
            onChange={(e) => note.setDraft(e.target.value)}
          />
          {note.restored ? <div className="text-xs text-ink-low mt-1">Restored your unsent note.</div> : null}
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-second btn-sm"
              disabled={!note.draft.trim() || finish.isPending}
              onClick={() => void requestChanges()}
            >
              Send back
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setComposing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BundleTaskCard({ task }: { task: HumanTask }) {
  const [nowMs] = useState(() => Date.now());
  const actions = useTaskActions();
  const toast = useToast();
  return (
    <TaskCard
      task={task}
      nowMs={nowMs}
      busy={actions.busyId === task.task_id}
      onPrimary={() =>
        void openPrimary(task).then((r) => {
          if (r.error) toast.error(r.error);
        })
      }
      onClose={() => void actions.complete(task)}
      onDecline={(reason) => actions.decline(task, reason)}
    />
  );
}
